import React, { ReactNode, CSSProperties, InputHTMLAttributes, useState } from 'react';
import type { BadgeTone, ThemeColors } from '../../types';
import { BRAND, BRAND_LIGHT, BRAND_DARK, getThemeColors } from '../../lib/theme';
import { Icon } from '../icons';
import { AppLink } from '../../lib/openLink';
import { useAdminAvatar, usePersonAvatar } from '../../lib/avatars';

// ─── Badge ─────────────────────────────────────────────────────────────────

interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  dark?: boolean;
}

export function Badge({ children, tone = 'neutral', dark = false }: BadgeProps) {
  const tones: Record<BadgeTone, { bg: string; color: string }> = {
    neutral: { bg: dark ? '#1e2735' : '#f2f4f7', color: dark ? '#cfd7e3' : '#344054' },
    success: { bg: dark ? '#0d2a1e' : '#ecfdf3', color: dark ? '#6ee7a8' : '#027a48' },
    warning: { bg: dark ? '#2c2010' : '#fffaeb', color: dark ? '#fbc97a' : '#b54708' },
    error:   { bg: dark ? '#2a1212' : '#fef3f2', color: dark ? '#fca5a1' : '#b42318' },
    brand:   { bg: dark ? '#2a1222' : '#fce7f4', color: dark ? '#f49dd1' : BRAND },
    info:    { bg: dark ? '#0d1d2e' : '#ecf3ff', color: dark ? '#93b4ff' : '#2a31d8' },
    blue:    { bg: dark ? '#0d1e2e' : '#f0f9ff', color: dark ? '#7cd4fd' : '#026aa2' },
  };
  const st = tones[tone] ?? tones.neutral;
  return (
    <span style={{
      background: st.bg, color: st.color,
      borderRadius: 9999, padding: '2px 9px',
      fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}

// ─── Card ──────────────────────────────────────────────────────────────────

interface CardProps {
  children: ReactNode;
  dark?: boolean;
  style?: CSSProperties;
  onClick?: () => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

export function Card({ children, dark = false, style = {}, onClick, onMouseEnter, onMouseLeave }: CardProps) {
  const t = getThemeColors(dark);
  return (
    <div
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        background: t.card,
        border: `1px solid ${t.border}`,
        borderRadius: 16,
        boxShadow: dark ? 'none' : '0 1px 2px rgba(16,24,40,0.04)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── Avatar ────────────────────────────────────────────────────────────────

interface AvatarProps {
  /** Darf zur Laufzeit null/undefined sein (API-Zeilen ohne Namen) → Fallback "?". */
  name: string | null | undefined;
  size?: number;
  tone?: 'brand' | 'blue' | 'green' | 'amber';
  dark?: boolean;
  /** Feedback 11.10.2026 — Bild-URL; ohne/defekt → Initialen. */
  src?: string | null;
  /** Bild einer Person (users.id) automatisch nachladen. */
  userId?: number | null;
  /** Bild eines Mitarbeiters (admins.id) automatisch nachladen. */
  adminId?: number | null;
}

export function Avatar({ name, size = 36, tone = 'brand', dark: _dark = false, src, userId, adminId }: AvatarProps) {
  const tones = {
    brand: { bg: BRAND_LIGHT, color: BRAND },
    blue:  { bg: '#ecf3ff', color: '#2a31d8' },
    green: { bg: '#ecfdf3', color: '#027a48' },
    amber: { bg: '#fffaeb', color: '#b54708' },
  };
  const st = tones[tone] ?? tones.brand;
  const personUrl = usePersonAvatar(src ? null : userId);
  const adminUrl = useAdminAvatar(src || userId ? null : adminId);
  const imageUrl = src || personUrl || adminUrl || null;
  const [failed, setFailed] = useState<string | null>(null);
  // Regression 22.09.2026: /v2/admin/dashboard/call-list lieferte name:null →
  // name.split warf und riss das gesamte Dashboard weiß. Nie mehr throwen.
  const safeName = typeof name === 'string' ? name.trim() : '';
  const ini = safeName
    ? safeName.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';
  const showImage = !!imageUrl && failed !== imageUrl;
  return (
    <div
      data-testid="avatar"
      style={{
        width: size, height: size, borderRadius: '50%',
        background: st.bg, color: st.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.36, fontWeight: 600, flexShrink: 0, overflow: 'hidden',
      }}
    >
      {showImage
        ? <img src={imageUrl as string} alt={safeName} onError={() => setFailed(imageUrl)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        : ini}
    </div>
  );
}

// ─── Button ────────────────────────────────────────────────────────────────

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  children?: ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  dark?: boolean;
  icon?: ReactNode;
  size?: 'sm' | 'md';
  style?: CSSProperties;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  /** Optional: Navigations-Button als echter Link rendern (Strg/Cmd-Klick, Mittelklick, "In neuem Tab öffnen"). */
  href?: string;
}

export function Button({
  variant = 'primary', children, onClick, dark = false,
  icon, size = 'md', style = {}, disabled = false, type = 'button', href,
}: ButtonProps) {
  const t = getThemeColors(dark);
  const pad = size === 'sm' ? '7px 12px' : '9px 16px';
  const fs = size === 'sm' ? 12 : 13;
  const styles: Record<string, CSSProperties> = {
    primary:   { background: BRAND, color: '#fff', border: 'none' },
    secondary: { background: t.card, color: t.text, border: `1px solid ${t.border}` },
    ghost:     { background: 'transparent', color: t.text, border: 'none' },
    danger:    { background: '#fef3f2', color: '#b42318', border: '1px solid #fecdca' },
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (variant === 'primary') (e.currentTarget as HTMLButtonElement).style.background = BRAND_DARK;
    else if (variant === 'secondary') (e.currentTarget as HTMLButtonElement).style.background = t.hover;
  };
  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (variant === 'primary') (e.currentTarget as HTMLButtonElement).style.background = BRAND;
    else if (variant === 'secondary') (e.currentTarget as HTMLButtonElement).style.background = t.card;
  };

  const btnStyle: CSSProperties = {
    ...(styles[variant] ?? styles.primary),
    borderRadius: 8, padding: pad, fontSize: fs,
    fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 7,
    transition: 'background 0.15s', whiteSpace: 'nowrap',
    opacity: disabled ? 0.65 : 1,
    ...style,
  };

  if (href && !disabled) {
    return (
      <AppLink
        to={href}
        onClick={onClick as unknown as React.MouseEventHandler<HTMLAnchorElement> | undefined}
        onMouseEnter={handleMouseEnter as unknown as React.MouseEventHandler<HTMLAnchorElement>}
        onMouseLeave={handleMouseLeave as unknown as React.MouseEventHandler<HTMLAnchorElement>}
        style={{ ...btnStyle, textDecoration: 'none', boxSizing: 'border-box' }}
      >
        {icon}
        {children}
      </AppLink>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={btnStyle}
    >
      {icon}
      {children}
    </button>
  );
}

// ─── Field ────────────────────────────────────────────────────────────────

interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}

export function Field({ label, hint, error, children }: FieldProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && (
        <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-gray-700)' }}>
          {label}
        </label>
      )}
      {children}
      {hint && !error && <p style={{ fontSize: 11, color: '#98a2b3', margin: 0 }}>{hint}</p>}
      {error && <p style={{ fontSize: 11, color: '#f04438', margin: 0 }}>{error}</p>}
    </div>
  );
}

// ─── Input ────────────────────────────────────────────────────────────────

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  dark?: boolean;
}

export function Input({ dark = false, style, onFocus, onBlur, ...props }: InputProps) {
  const t = getThemeColors(dark);
  const [focus, setFocus] = useState(false);
  return (
    <input
      {...props}
      onFocus={(e) => { setFocus(true); onFocus && onFocus(e); }}
      onBlur={(e) => { setFocus(false); onBlur && onBlur(e); }}
      style={{
        width: '100%',
        border: `1px solid ${focus ? BRAND : t.border}`,
        borderRadius: 9,
        padding: '9px 12px',
        fontSize: 13,
        color: t.heading,
        background: t.inputBg,
        boxShadow: focus ? '0 0 0 3px rgba(181,41,122,0.10)' : 'none',
        transition: 'border 0.15s, box-shadow 0.15s',
        fontFamily: 'Outfit, sans-serif',
        ...style,
      }}
    />
  );
}

// ─── Toggle ───────────────────────────────────────────────────────────────

interface ToggleProps {
  checked: boolean;
  onChange: (value: boolean) => void;
}

export function Toggle({ checked, onChange }: ToggleProps) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 40, height: 22, borderRadius: 9999,
        background: checked ? BRAND : '#d0d5dd',
        position: 'relative', cursor: 'pointer',
        transition: 'background 0.2s', flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute', top: 2, left: checked ? 20 : 2,
        width: 18, height: 18, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }}/>
    </div>
  );
}

// ─── InfoTile / InfoCell ───────────────────────────────────────────────────

interface InfoTileProps {
  icon?: ReactNode;
  label: string;
  value: string;
  dark?: boolean;
}

export function InfoTile({ icon, label, value, dark = false }: InfoTileProps) {
  const t = getThemeColors(dark);
  return (
    <div style={{ background: t.cardMuted, border: `1px solid ${t.borderSoft}`, borderRadius: 10, padding: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: t.muted, marginBottom: 4 }}>
        {icon}{label}
      </div>
      <div style={{ fontSize: 13, color: t.heading, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

export function InfoCell({ label, value, dark = false }: { label: string; value: string; dark?: boolean }) {
  const t = getThemeColors(dark);
  return (
    <div style={{ background: t.cardMuted, border: `1px solid ${t.borderSoft}`, borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 11, color: t.muted, marginBottom: 4, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 13, color: t.heading, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

// ─── SectionTitle ─────────────────────────────────────────────────────────

interface SectionTitleProps {
  title: string;
  subtitle?: string;
  dark?: boolean;
}

export function SectionTitle({ title, subtitle, dark = false }: SectionTitleProps) {
  const t = getThemeColors(dark);
  return (
    <div style={{ marginBottom: 20, paddingBottom: 14, borderBottom: `1px solid ${t.borderSoft}` }}>
      <div style={{ fontSize: 16, fontWeight: 600, color: t.heading }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12, color: t.muted, marginTop: 3 }}>{subtitle}</div>}
    </div>
  );
}

// ─── Drawer ────────────────────────────────────────────────────────────────

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /**
   * Maximale Drawer-Breite. Zahl wird zu `${n}px`. String erlaubt
   * `'100vw'` o.ae. fuer Mobile-Full-Screen-Drawer. Der innere Panel
   * hat immer width:100%, sodass maxWidth den effektiv sichtbaren
   * Wert begrenzt.
   */
  width?: number | string;
  dark?: boolean;
  children: ReactNode;
}

export function Drawer({ open, onClose, width = 520, dark = false, children }: DrawerProps) {
  const t = getThemeColors(dark);
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(16,24,40,0.35)',
        zIndex: 1000, display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="fade-in"
        role="dialog"
        aria-modal="true"
        style={{
          // Desktop: configured width; mobile (≤768px): full viewport width
          // via maxWidth cap so the drawer never overflows narrow screens.
          width: '100%',
          maxWidth: width,
          background: t.card,
          height: '100%',
          display: 'flex', flexDirection: 'column',
          borderLeft: `1px solid ${t.border}`,
        }}
      >
        {children}
      </div>
    </div>
  );
}

// Re-export Icon for convenience
export { Icon };

// Re-export EditDrawer
export { EditDrawer } from './EditDrawer';
export type { EditDrawerProps } from './EditDrawer';
